/** @format */

import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import CustomModal from "../../../common-utilities/core/modal";
import MuiTable from "../../../common-utilities/core/mui-table";
import { invokeApi, invokeUploadFile, HTTP_METHODS } from "../../utils/http-service";
import { CF_APPROVAL_HOSTNAME } from "../../../common-utilities/utils/end-points";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";

// Which master (masterType path param, per the API contract) each tab manages, and which
// REST_URLS entry (added in end-points/cf-approval.js) holds its base path.
const MASTERS = [
  {
    masterType: "input-fields",
    label: "Input Options",
    urlKey: "CAPEX_INPUT_MASTER",
    // Temporarily hidden (UI-only -- upload still works if re-enabled): this master drives the
    // Input Form's structure (fieldId/inputType/showWhen), not plain reference numbers, so a bulk
    // Excel replace here is riskier than on the pure lookup tables below.
    hideUpload: true,
    filterFields: [
      { key: "category", label: "Category" },
      { key: "fieldLabel", label: "Field Label" },
    ],
  },
  { masterType: "rates", label: "Rate Master", urlKey: "CAPEX_RATE_MASTER" },
  { masterType: "boq", label: "BOQ Master", urlKey: "CAPEX_BOQ_MASTER" },
  { masterType: "aop-budget", label: "AOP Budget Master", urlKey: "CAPEX_AOP_MASTER" },
  {
    masterType: "output-templates",
    label: "Output Templates",
    urlKey: "CAPEX_OUTPUT_TEMPLATE_MASTER",
    // Same reasoning as input-fields above -- this master drives the calculation engine's
    // formulaType/formulaParams, not plain reference numbers.
    hideUpload: true,
  },
];

// Keys that are bookkeeping, not master-data fields — never shown/edited as a row field.
// `archive` is the soft-delete flag (managed by the Delete action, not hand-edited).
const META_KEYS = ["id", "_id", "__v", "createdAt", "updatedAt", "masterType", "archive"];

const fieldKeysFromRow = (row) => Object.keys(row || {}).filter((k) => !META_KEYS.includes(k));

// Some master rows carry object/array fields (e.g. input-fields' `showWhen: {fieldId, valueIn}`)
// that MuiTable can't render as a raw cell value — format anything non-primitive as JSON text.
// On the Rate Master tab specifically, a row whose `unit` reads e.g. "% of LHI-GC" stores its
// `rate` as a plain number (5, not 0.05 — see capex-number.js's parseIndianNumber) purely for
// calc-engine simplicity; append "%" here so it isn't misread as a flat currency amount.
const formatCellValue = (value, { masterType, key, row } = {}) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  if (masterType === "rates" && key === "rate" && String(row?.unit || "").includes("%")) {
    return `${value}%`;
  }
  return String(value);
};

const inferFieldType = (value) => {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  if (value !== null && typeof value === "object") return "json";
  return "text";
};

const blankValueFor = (type) => {
  if (type === "boolean") return false;
  if (type === "number") return "";
  if (type === "json") return "";
  return "";
};

function TabPanel({ children, value, index }) {
  return <div hidden={value !== index}>{value === index && <Box>{children}</Box>}</div>;
}

/**
 * Row add/edit modal. The exact per-master row schema isn't part of the fixed API contract
 * (only the CRUD endpoint shapes are), so this derives its fields from whatever keys already
 * exist on real data:
 *   - Editing a row: one input per key already on that row (type inferred from its value).
 *   - Adding a row when the master already has data: same field set as its first row, blank.
 *   - Adding a row to a completely empty master: falls back to a raw-JSON textarea (like the
 *     editableFilesObject editor in setup/step-master.js) since there's no sample row to shape
 *     a form from yet.
 */
const RowEditModal = ({ label, sampleRow, editingRow, onClose, onSave, saving }) => {
  const isEdit = !!editingRow;
  const schemaSourceRow = isEdit ? editingRow : sampleRow;
  const jsonMode = !isEdit && !sampleRow;

  const fieldTypes = {};
  const initialValues = {};
  if (!jsonMode) {
    fieldKeysFromRow(schemaSourceRow).forEach((key) => {
      const type = inferFieldType(schemaSourceRow[key]);
      fieldTypes[key] = type;
      initialValues[key] =
        isEdit && schemaSourceRow[key] !== undefined
          ? type === "json"
            ? JSON.stringify(schemaSourceRow[key], null, 2)
            : schemaSourceRow[key]
          : blankValueFor(type);
    });
  }

  const [values, setValues] = useState(jsonMode ? { json: "{\n  \n}" } : initialValues);

  const setField = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    if (jsonMode) {
      let payload;
      try {
        payload = JSON.parse(values.json);
      } catch (e) {
        toast.error("Row must be valid JSON");
        return;
      }
      onSave(payload);
      return;
    }

    const payload = {};
    Object.keys(fieldTypes).forEach((key) => {
      const type = fieldTypes[key];
      const raw = values[key];
      if (type === "number") {
        payload[key] = raw === "" ? null : Number(raw);
      } else if (type === "json") {
        try {
          payload[key] = raw ? JSON.parse(raw) : null;
        } catch (e) {
          payload[key] = raw;
        }
      } else {
        payload[key] = raw;
      }
    });
    onSave(payload);
  };

  return (
    <CustomModal onClose={onClose} title={isEdit ? `Edit ${label} Row` : `Add ${label} Row`} size="md">
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {jsonMode ? (
          <>
            <Typography variant="caption" color="text.secondary">
              This master has no rows yet, so there's no sample to build a form from. Enter the
              row as JSON (matching whatever fields the backend expects for this master) — or
              upload an Excel file instead and edit rows in-form afterwards.
            </Typography>
            <TextField
              label="Row JSON"
              multiline
              minRows={8}
              value={values.json}
              onChange={(e) => setField("json", e.target.value)}
              sx={{ "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
            />
          </>
        ) : (
          Object.keys(fieldTypes).map((key) => {
            const type = fieldTypes[key];
            if (type === "boolean") {
              return (
                <FormControl key={key} size="small" fullWidth>
                  <Typography variant="caption" sx={{ mb: 0.5 }}>
                    {key}
                  </Typography>
                  <Select value={!!values[key]} onChange={(e) => setField(key, e.target.value)}>
                    <MenuItem value={true}>True</MenuItem>
                    <MenuItem value={false}>False</MenuItem>
                  </Select>
                </FormControl>
              );
            }
            if (type === "json") {
              return (
                <TextField
                  key={key}
                  label={key}
                  multiline
                  minRows={3}
                  size="small"
                  fullWidth
                  value={values[key] ?? ""}
                  onChange={(e) => setField(key, e.target.value)}
                  sx={{ "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
                />
              );
            }
            return (
              <TextField
                key={key}
                label={key}
                type={type === "number" ? "number" : "text"}
                size="small"
                fullWidth
                value={values[key] ?? ""}
                onChange={(e) => setField(key, e.target.value)}
              />
            );
          })
        )}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </Stack>
      </Box>
    </CustomModal>
  );
};

const PAGE_LIMIT = 20;

const CapexMasterTab = ({ masterType, label, urlKey, filterFields, hideUpload }) => {
  const baseUrl = `${CF_APPROVAL_HOSTNAME}${REST_URLS[urlKey]}`;
  const [data, setData] = useState({ results: [], page: 1, totalPages: 1, totalResults: 0 });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [modalRow, setModalRow] = useState(undefined); // undefined = closed, null = add, object = edit
  const [saving, setSaving] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const fileInputRef = useRef(null);

  // `filters` defaults to the current filterValues state, but callers that just changed it
  // (Search/Clear) pass the new values explicitly rather than waiting a render for state to settle.
  const loadData = (page = 1, filters = filterValues) => {
    setLoading(true);
    const params = { page, limit: PAGE_LIMIT };
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
    invokeApi(HTTP_METHODS.GET, baseUrl, null, params)
      .then((response) => {
        setData({
          results: response?.results || [],
          page: response?.page || 1,
          totalPages: response?.totalPages || 1,
          totalResults: response?.totalResults ?? (response?.results || []).length,
        });
      })
      .catch(() => toast.error(`Failed to load ${label}`))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setFilterValues({});
    loadData(1, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterType]);

  const handleApplyFilters = () => loadData(1, filterValues);
  const handleClearFilters = () => {
    setFilterValues({});
    loadData(1, {});
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    invokeUploadFile(`${baseUrl}/upload`, formData)
      .then((response) => {
        const count = response?.data?.count;
        toast.success(count != null ? `Uploaded ${count} rows` : `${label} uploaded successfully`);
        loadData(1);
      })
      .catch(() => toast.error(`Failed to upload ${label}`))
      .finally(() => setUploading(false));
  };

  const handleDelete = (row) => {
    const id = row.id ?? row._id;
    if (!id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete this ${label} row?`)) return;
    invokeApi(HTTP_METHODS.DELETE, `${baseUrl}/${id}`)
      .then(() => {
        toast.success("Row deleted");
        loadData(data.page);
      })
      .catch((err) => toast.error(err?.message || "Failed to delete row"));
  };

  const handleSaveRow = (payload) => {
    setSaving(true);
    const isEdit = !!modalRow;
    const id = isEdit ? modalRow.id ?? modalRow._id : null;
    const request = isEdit
      ? invokeApi(HTTP_METHODS.PATCH, `${baseUrl}/${id}`, payload)
      : invokeApi(HTTP_METHODS.POST, baseUrl, payload);

    request
      .then(() => {
        toast.success(isEdit ? "Row updated" : "Row added");
        setModalRow(undefined);
        loadData(isEdit ? data.page : 1);
      })
      .catch((err) => toast.error(err?.message || "Failed to save row"))
      .finally(() => setSaving(false));
  };

  // Union keys across every row on the current page, not just the first row — a single row
  // (e.g. one without `showWhen`) shouldn't hide a column that other rows on the same page have.
  const columnKeys = Array.from(
    data.results.reduce((keys, row) => {
      fieldKeysFromRow(row).forEach((k) => keys.add(k));
      return keys;
    }, new Set())
  );
  const sampleRow = data.results[0];

  const columns = [
    ...columnKeys.map((key) => ({
      label: key,
      id: key,
      render: (row) => formatCellValue(row[key], { masterType, key, row }),
    })),
    {
      label: "Actions",
      id: "__actions",
      render: (row) => (
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <IconButton size="small" onClick={() => setModalRow(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      {filterFields?.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {filterFields.map((f) => (
            <TextField
              key={f.key}
              label={f.label}
              size="small"
              value={filterValues[f.key] || ""}
              onChange={(e) => setFilterValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyFilters();
              }}
            />
          ))}
          <Button size="small" variant="outlined" onClick={handleApplyFilters}>
            Search
          </Button>
          <Button size="small" onClick={handleClearFilters}>
            Clear
          </Button>
        </Stack>
      )}
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 2 }}>
        {!hideUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <Button variant="outlined" disabled={uploading} onClick={() => fileInputRef.current.click()}>
              {uploading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              {uploading ? "Uploading..." : "Upload Excel (replaces all rows)"}
            </Button>
          </>
        )}
        <Button variant="contained" onClick={() => setModalRow(null)}>
          + Add Row
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : data.results.length ? (
        <MuiTable
          columnsList={columns}
          dataList={data.results}
          pageCount={data.totalPages}
          filters={{ page: data.page }}
          onChange={(page) => loadData(page)}
          tableConfig={{ pagination: true, sorting: false }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No {label} rows yet. Upload an Excel file or add a row.
        </Typography>
      )}

      {modalRow !== undefined && (
        <RowEditModal
          label={label}
          sampleRow={sampleRow}
          editingRow={modalRow}
          saving={saving}
          onClose={() => setModalRow(undefined)}
          onSave={handleSaveRow}
        />
      )}
    </Box>
  );
};

/**
 * Admin screen for the Capex Calculator's master/lookup tables — one tab per master, each with
 * an Excel-upload (full replace) button and an in-UI paginated list with add/edit/delete.
 * Wired into the existing CF Approval v2 Setup tab strip (see setup/index.js).
 */
const CapexMasterData = () => {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {MASTERS.map((m) => (
            <Tab key={m.masterType} label={m.label} />
          ))}
        </Tabs>
      </Box>
      {MASTERS.map((m, index) => (
        <TabPanel key={m.masterType} value={tab} index={index}>
          <CapexMasterTab
            masterType={m.masterType}
            label={m.label}
            urlKey={m.urlKey}
            filterFields={m.filterFields}
            hideUpload={m.hideUpload}
          />
        </TabPanel>
      ))}
    </Box>
  );
};

export default CapexMasterData;
